/**
 * useCombinePreview — debounced (300 ms) TanStack Query over
 * `POST /imports/combine/preview`, keyed by the WHOLE recipe (sources,
 * filters, "only in every list", exclusions, page, view) so any change
 * refetches. `placeholderData: keepPreviousData` keeps the previous funnel +
 * table on screen while a refined recipe is in flight — the same
 * anti-flicker shape `SearchTypeahead` and `ImportDetailPage`'s contacts
 * query already use.
 *
 * The debounce is on the INPUT, not the query: the caller's `recipe` is held
 * in local state and only committed after `DEBOUNCE_MS` of no further
 * change, so a fast typist in the Title filter (or quick successive list
 * picks) fires one request, not one per keystroke.
 *
 * Bug hunt 2026-09-23 (F1): the previous preview is kept ONLY while the
 * query is enabled. Removing the last source (or "Start over") disables it,
 * and `preview` is then `undefined` — before, `keepPreviousData` went on
 * serving the old funnel, table and Compare with Thrive status for lists
 * no longer selected. `isUpdating` (F9) is true while what is on screen does
 * not answer the current recipe yet: the debounce is pending, no answer has
 * landed, or the data is the previous recipe's placeholder (a re-read of the
 * same recipe is `isFetching`, not `isUpdating`; sub-task 02 of epic
 * 2026-10-01-combine-stability-perf). `answered` is the request the shown
 * answer is for.
 *
 * Retry (PR #962 follow-up F1): the query does NOT inherit the app-wide
 * `retry: 1` (`main.tsx`). A 503 comes after the server waited up to
 * `COMBINE_MERGE_WAIT_SECONDS` (45 s) for a merge slot; resending it queued
 * the request again behind the same full queue, ignored `Retry-After`, and
 * held the busy message back for up to twice as long. No other HTTP answer
 * (413, 409, 422, 500) changes on a resend either, and a recipe change
 * re-fetches anyway, so only a failure with no HTTP answer is retried, once.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
  type CombinePreviewRequest,
  type CombinePreviewResponse,
  ImportsApiError,
  type ImportsClient,
} from "../../../clients/imports.client";

const DEBOUNCE_MS = 300;

/** The preview query's `retry`: never an HTTP answer (`ImportsApiError`),
 *  and a network failure once. */
export function retryCombinePreview(failureCount: number, error: unknown): boolean {
  return failureCount < 1 && !(error instanceof ImportsApiError);
}

export interface UseCombinePreviewArgs {
  client: ImportsClient;
  /** The whole recipe (+ pagination) — build with `useMemo` keyed on its own
   *  primitive parts so this hook's debounce effect does not reset on every
   *  unrelated re-render of the caller. */
  recipe: CombinePreviewRequest;
  /** Below `MIN_SOURCES` (1 — a single source list is a legitimate way to
   *  just FILTER it, product decision 2026-09-23; the server's own
   *  `source_import_ids` min_length matches) there is nothing to preview —
   *  callers pass `false` until the picker has at least one selection. */
  enabled?: boolean;
}

/** One preview answer and the exact request it answers, so a caller can
 *  tell whether what is on screen (possibly the previous recipe's
 *  placeholder) is about the target search it shows. */
export interface CombinePreviewAnswer {
  request: CombinePreviewRequest;
  response: CombinePreviewResponse;
}

export function useCombinePreview({ client, recipe, enabled = true }: UseCombinePreviewArgs) {
  const [debounced, setDebounced] = useState(recipe);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(recipe), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [recipe]);

  const isEnabled = enabled && debounced.source_import_ids.length >= 1;
  // The key is the recipe itself, so a superseded recipe is another query:
  // its answer can only land in its own cache entry, never on screen as the
  // current one, and TanStack aborts its request (`signal`) once the page
  // stops observing it (sub-task 02 of epic 2026-10-01-combine-stability-perf).
  const query = useQuery<CombinePreviewAnswer>({
    queryKey: ["combine-preview", debounced],
    queryFn: async ({ signal }) => ({
      request: debounced,
      response: await client.combinePreview(debounced, signal),
    }),
    enabled: isEnabled,
    retry: retryCombinePreview,
    placeholderData: isEnabled ? keepPreviousData : undefined,
  });

  const preview = isEnabled ? query.data?.response : undefined;
  /** The request the preview on screen answers — the previous recipe's
   *  while `isPlaceholderData`. */
  const answered = isEnabled ? query.data?.request : undefined;
  // What is on screen does not answer the current recipe: the debounce is
  // pending, the data is the previous recipe's placeholder, or nothing has
  // landed yet. A background read of the SAME recipe (the target-check
  // poll, a refetch after a Compare) is not "updating": the answer on screen
  // is still this recipe's, so Generate's reason does not flicker on each
  // poll (`isFetching` says when a read is in flight).
  const isUpdating =
    enabled &&
    (recipe !== debounced ||
      query.isPlaceholderData ||
      (query.isFetching && query.data === undefined));
  return { query, preview, answered, isUpdating, isFetching: enabled && query.isFetching };
}
